import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion, useDragControls, useInView, type PanInfo } from "framer-motion";
import { FiArrowRight, FiArrowUpRight, FiCheck, FiCopy, FiMail, FiX } from "react-icons/fi";
import { BsGithub } from "react-icons/bs";
import { FaLinkedinIn } from "react-icons/fa";
import { IoBatteryFull, IoCellular, IoWifi } from "react-icons/io5";
import { SiApple, SiX } from "react-icons/si";
import type { IconType } from "react-icons";
import { TitleHeading } from "@/components/helper/TitleHeading";
import { BaseSection, type BaseSectionProps } from "@/components/sections/BaseSection";
import { APP_PLATFORMS, APPS, filterApps, isInSiteRoute, type AppEntry, type AppPlatform } from "@/config/apps";
import { useClock } from "@/hooks/useClock";
import { useMatchMedia } from "@/hooks/useMatchMedia";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useScrollLock } from "@/hooks/useScrollLock";
import { getSocialMediaLink } from "@/utils/external-links";
import "@/styles/apps.css";

/** Which device the section is drawn as; breakpoints follow the site's 770px mobile and 1100px nav switch. */
type Device = "phone" | "tablet" | "mac";

type DockLink = { id: "github" | "x" | "linkedin" | "email"; href: string; icon: IconType };

const DOCK_LINKS: ReadonlyArray<DockLink> = [
    { id: "github", href: getSocialMediaLink("github"), icon: BsGithub },
    { id: "x", href: getSocialMediaLink("x"), icon: SiX },
    { id: "linkedin", href: getSocialMediaLink("linkedin"), icon: FaLinkedinIn },
    { id: "email", href: `mailto:${getSocialMediaLink("email")}`, icon: FiMail },
];

export const Apps = (props: BaseSectionProps) => {
    const { t } = useTranslation();
    const isPhone = useMatchMedia("(max-width: 770px)");
    const isMac = useMatchMedia("(min-width: 1100px)");
    const device: Device = isPhone ? "phone" : isMac ? "mac" : "tablet";
    const reducedMotion = usePrefersReducedMotion();
    // The device powers on (and the MacBook lid opens) while it is on screen,
    // and shuts down again when scrolled away.
    const deviceRef = useRef<HTMLDivElement>(null);
    const deviceInView = useInView(deviceRef, { amount: 0.35 });
    const on = deviceInView || reducedMotion;
    const [openId, setOpenId] = useState<AppEntry["id"] | null>(null);
    const close = useCallback(() => setOpenId(null), []);
    const openApp = APPS.find((app) => app.id === openId) ?? null;
    const [platform, setPlatform] = useState<AppPlatform | "all">("all");
    const apps = useMemo(() => filterApps(APPS, platform), [platform]);

    const screen = <HomeScreen device={device} on={on} apps={apps} onOpen={setOpenId} />;

    return (
        <BaseSection {...props}>
            <div className="apps-head section-head-split">
                <div>
                    <TitleHeading>{t("apps.title")}</TitleHeading>
                    <h2 className="section-subtitle">{t("apps.subtitle")}</h2>
                </div>
                <p className="section-lede">{t("apps.description")}</p>
            </div>

            <AppsFilter platform={platform} onPlatform={setPlatform} count={apps.length} />

            <div ref={deviceRef}>
                {device === "phone" && <PhoneFrame on={on}>{screen}</PhoneFrame>}
                {device === "tablet" && <TabletFrame on={on}>{screen}</TabletFrame>}
                {device === "mac" && <MacFrame on={on}>{screen}</MacFrame>}
            </div>

            <AppSheet app={openApp} onClose={close} asSheet={isPhone} />
        </BaseSection>
    );
};

/** Seconds the MacBook lid takes to open; the screen powers on once it is mostly up. */
const LID_DURATION = 0.9;

/** Apps per home-screen page (columns x rows that fit the screen); extra apps spill onto pages you swipe to. */
function usePageSize(device: Device): number {
    const isNarrowPhone = useMatchMedia("(max-width: 360px)");
    if (device === "phone") return (isNarrowPhone ? 3 : 4) * 4;
    if (device === "tablet") return 6 * 2;
    return APPS.length;
}

/**
 * Platform filter, kept outside the device so the home screen stays a clean
 * mock-up. It only offers platforms some app ships on.
 */
function AppsFilter({
    platform,
    onPlatform,
    count,
}: {
    platform: AppPlatform | "all";
    onPlatform: (platform: AppPlatform | "all") => void;
    count: number;
}) {
    const { t } = useTranslation();
    const options: ReadonlyArray<AppPlatform | "all"> = ["all", ...APP_PLATFORMS];

    return (
        <div className="apps-filter-wrap">
            <div className="apps-filter" role="group" aria-label={t("apps.filter.label")}>
                {options.map((option) => (
                    <button
                        key={option}
                        type="button"
                        className={`apps-filter-chip${platform === option ? " is-active" : ""}`}
                        aria-pressed={platform === option}
                        onClick={() => onPlatform(option)}
                    >
                        {option === "all" ? t("apps.filter.all") : t(`apps.platforms.${option}`)}
                    </button>
                ))}
            </div>
            <p className="sr-only" aria-live="polite">
                {t("apps.filter.count", { count })}
            </p>
        </div>
    );
}

function chunk<T>(items: ReadonlyArray<T>, size: number): T[][] {
    const pages: T[][] = [];
    for (let i = 0; i < items.length; i += size) pages.push(items.slice(i, i + size));
    return pages;
}

function StatusBar({ device }: { device: Device }) {
    const { t, i18n } = useTranslation();
    const now = useClock();
    const time = now?.toLocaleTimeString(i18n.language, { hour: "numeric", minute: "2-digit" }) ?? "";

    if (device === "mac") {
        const day = now?.toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" }) ?? "";
        return (
            <div className="device-menubar" aria-hidden="true">
                <span className="device-menubar-left">
                    <SiApple />
                    <strong>{t("apps.title")}</strong>
                    <span>{t("apps.menu.file")}</span>
                    <span>{t("apps.menu.view")}</span>
                    <span>{t("apps.menu.window")}</span>
                </span>
                <span className="device-menubar-right">
                    <IoWifi />
                    <IoBatteryFull />
                    <span>{day}</span>
                    <span>{time}</span>
                </span>
            </div>
        );
    }

    return (
        <div className={`device-statusbar device-statusbar-${device}`} aria-hidden="true">
            <span className="device-time">{time}</span>
            <span className="device-status-icons">
                <IoCellular />
                <IoWifi />
                <IoBatteryFull />
            </span>
        </div>
    );
}

function HomeScreen({
    device,
    on,
    apps: visibleApps,
    onOpen,
}: {
    device: Device;
    on: boolean;
    apps: ReadonlyArray<AppEntry>;
    onOpen: (id: AppEntry["id"]) => void;
}) {
    const { t } = useTranslation();
    const reducedMotion = usePrefersReducedMotion();
    const pages = chunk(visibleApps, usePageSize(device));
    const pagerRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(0);
    const [launchingId, setLaunchingId] = useState<AppEntry["id"] | null>(null);
    const launchTimer = useRef<number>();

    useEffect(() => () => window.clearTimeout(launchTimer.current), []);

    // A new filter lands back on the first page, like a fresh home screen.
    useEffect(() => {
        if (pagerRef.current) pagerRef.current.scrollLeft = 0;
        setPage(0);
    }, [visibleApps]);

    // Icons pop in like a home screen after unlock, once the screen is on (and,
    // on the MacBook, once the lid is up). They blink out with the screen.
    const baseDelay = device === "mac" ? LID_DURATION * 0.8 : 0.35;
    const reveal = (index: number) => ({
        initial: reducedMotion ? false : { opacity: 0, scale: 0.6 },
        animate: on ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 },
        transition: on
            ? { duration: 0.45, ease: [0.34, 1.56, 0.64, 1], delay: reducedMotion ? 0 : baseDelay + index * 0.05 }
            : { duration: 0.2, delay: 0.25 },
    });

    const onPagerScroll = () => {
        const el = pagerRef.current;
        if (el && el.clientWidth > 0) setPage(Math.round(el.scrollLeft / el.clientWidth));
    };

    const goToPage = (index: number) => {
        const el = pagerRef.current;
        el?.scrollTo({ left: index * el.clientWidth, behavior: reducedMotion ? "auto" : "smooth" });
    };

    // On the Mac the icon zooms out like a Launchpad launch before the sheet opens.
    const launch = (id: AppEntry["id"]) => {
        if (device !== "mac" || reducedMotion) {
            onOpen(id);
            return;
        }
        setLaunchingId(id);
        window.clearTimeout(launchTimer.current);
        launchTimer.current = window.setTimeout(() => {
            setLaunchingId(null);
            onOpen(id);
        }, 380);
    };

    const iconSize = device === "phone" ? "md" : "lg";

    return (
        <>
            <StatusBar device={device} />
            <div className="device-pager" ref={pagerRef} onScroll={onPagerScroll} role="group" aria-label={t("apps.screenAria")}>
                {pages.map((apps, pageIndex) => (
                    <ul
                        key={pageIndex}
                        className="device-grid"
                        aria-label={pages.length > 1 ? t("apps.pageAria", { page: pageIndex + 1, total: pages.length }) : undefined}
                    >
                        {apps.map((app, index) => (
                            <motion.li key={app.id} {...reveal(index)}>
                                <button
                                    type="button"
                                    className={`device-app${launchingId === app.id ? " is-launching" : ""}`}
                                    onClick={() => launch(app.id)}
                                    aria-haspopup="dialog"
                                    aria-label={t("apps.openDetailsAria", { name: app.name })}
                                >
                                    <span className="device-app-icon">
                                        <AppIcon app={app} size={iconSize} />
                                        {launchingId === app.id && (
                                            <span className="device-app-ghost" aria-hidden="true">
                                                <AppIcon app={app} size={iconSize} />
                                            </span>
                                        )}
                                    </span>
                                    <span className="device-app-name">{app.name}</span>
                                </button>
                            </motion.li>
                        ))}
                    </ul>
                ))}
            </div>
            {pages.length > 1 && (
                <div className="device-page-dots">
                    {pages.map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            className="device-page-dot"
                            aria-current={index === page ? "true" : undefined}
                            aria-label={t("apps.pageAria", { page: index + 1, total: pages.length })}
                            onClick={() => goToPage(index)}
                        />
                    ))}
                </div>
            )}
            <nav className="device-dock" aria-label={t("apps.dockAria")}>
                {DOCK_LINKS.map((link) => (
                    <DockItem key={link.id} link={link} />
                ))}
            </nav>
        </>
    );
}

/** Dock link; on the Mac it bounces when clicked and keeps a "running" dot, like a launched app. */
function DockItem({ link: { id, href, icon: Icon } }: { link: DockLink }) {
    const { t } = useTranslation();
    const [bouncing, setBouncing] = useState(false);
    const [running, setRunning] = useState(false);

    return (
        <a
            className={`device-dock-app${bouncing ? " is-bouncing" : ""}${running ? " is-running" : ""}`}
            href={href}
            target={id === "email" ? undefined : "_blank"}
            rel={id === "email" ? undefined : "noreferrer noopener"}
            aria-label={t(`apps.dock.${id}`)}
            data-label={t(`apps.dock.${id}`)}
            onClick={() => {
                setBouncing(true);
                setRunning(true);
            }}
            onAnimationEnd={() => setBouncing(false)}
        >
            <span className={`apps-icon apps-icon-md apps-icon-dock apps-icon-dock-${id}`} aria-hidden="true">
                <Icon />
            </span>
        </a>
    );
}

/** Black glass over the screen: opaque while the device is off, fading out as it powers on. */
function ScreenPower({ on, delay = 0 }: { on: boolean; delay?: number }) {
    const reducedMotion = usePrefersReducedMotion();
    return (
        <motion.span
            className="device-power"
            aria-hidden="true"
            initial={false}
            animate={{ opacity: on ? 0 : 1 }}
            transition={
                reducedMotion
                    ? { duration: 0 }
                    : on
                      ? { duration: 0.6, ease: "easeOut", delay }
                      : { duration: 0.35, ease: "easeIn" }
            }
            style={{ pointerEvents: on ? "none" : "auto" }}
        />
    );
}

function PhoneFrame({ on, children }: { on: boolean; children: ReactNode }) {
    return (
        <div className="device device-phone">
            <div className="device-screen">
                <span className="device-island" aria-hidden="true" />
                {children}
                <span className="device-homebar" aria-hidden="true" />
                <ScreenPower on={on} />
            </div>
        </div>
    );
}

function TabletFrame({ on, children }: { on: boolean; children: ReactNode }) {
    return (
        <div className="device device-tablet">
            <span className="device-camera" aria-hidden="true" />
            <div className="device-screen">
                {children}
                <span className="device-homebar" aria-hidden="true" />
                <ScreenPower on={on} />
            </div>
        </div>
    );
}

function MacFrame({ on, children }: { on: boolean; children: ReactNode }) {
    const reducedMotion = usePrefersReducedMotion();
    return (
        <div className="device device-mac">
            <div className="device-mac-stage">
                {/* Hinged at the bottom edge: folds down onto the base when closed.
                    Opening swings the lid up first; closing waits for the screen to go dark. */}
                <motion.div
                    className="device-mac-lid"
                    initial={false}
                    animate={{ rotateX: on ? 0 : -90 }}
                    transition={
                        reducedMotion
                            ? { duration: 0 }
                            : on
                              ? { duration: LID_DURATION, ease: [0.22, 1, 0.36, 1] }
                              : { duration: 0.7, ease: [0.55, 0, 0.75, 0.3], delay: 0.3 }
                    }
                >
                    <div className="device-screen">
                        <span className="device-notch" aria-hidden="true" />
                        {children}
                        <ScreenPower on={on} delay={LID_DURATION * 0.45} />
                    </div>
                </motion.div>
            </div>
            <div className="device-mac-base" aria-hidden="true">
                <span className="device-mac-scoop" />
            </div>
        </div>
    );
}

/**
 * The "Open"/"Get" call to action. GitHub apps and external sites open in a new tab;
 * an in-site route navigates in the same tab under the current locale.
 */
function AppLink({ app, className, children }: { app: AppEntry; className: string; children: ReactNode }) {
    const { locale } = useParams<{ locale: string }>();
    if (app.webUrl === undefined) {
        return (
            <a className={className} href={app.repoUrl} target="_blank" rel="noreferrer noopener">
                {children}
            </a>
        );
    }
    if (isInSiteRoute(app.webUrl)) {
        return (
            <Link className={className} to={`/${locale ?? "en"}${app.webUrl}`}>
                {children}
            </Link>
        );
    }
    return (
        <a className={className} href={app.webUrl} target="_blank" rel="noreferrer noopener">
            {children}
        </a>
    );
}

/** Rounded-square app icon: the repo's own artwork, or a drawn one for apps without any. */
export function AppIcon({ app, size = "md" }: { app: AppEntry; size?: "sm" | "md" | "lg" }) {
    const className = `apps-icon apps-icon-${size}${app.iconTone === "dark" ? " apps-icon-dark" : ""}`;
    if (app.icon) {
        return <img className={className} src={app.icon} alt="" width={256} height={256} loading="lazy" decoding="async" />;
    }
    return (
        <span className={`${className} apps-icon-pixel`} aria-hidden="true">
            <PixelAgent />
        </span>
    );
}

/** 8x8 pixel character for Agents Office, which draws agents as pixel people. */
function PixelAgent() {
    // Each string is one row; "#" is a filled pixel.
    const rows = ["..####..", "..####..", "...##...", ".######.", "#.####.#", "..####..", "..#..#..", ".##..##."];
    return (
        <svg viewBox="0 0 8 8" shapeRendering="crispEdges">
            {rows.flatMap((row, y) =>
                [...row].map((cell, x) => (cell === "#" ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} /> : null)),
            )}
        </svg>
    );
}

function StatusLabel({ app }: { app: AppEntry }) {
    const { t } = useTranslation();
    return (
        <span className="apps-status" data-status={app.status}>
            <span className="apps-status-dot" aria-hidden="true" />
            {t(`apps.status.${app.status}`)}
        </span>
    );
}

function useCopy(text: string) {
    const [copied, setCopied] = useState(false);
    const timer = useRef<number>();

    useEffect(() => () => window.clearTimeout(timer.current), []);

    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (error) {
            // Insecure context or a denied permission: the command stays visible
            // and selectable, so the visitor can still copy it by hand.
            console.warn("[Apps] clipboard write failed", error);
            return;
        }
        setCopied(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1600);
    }, [text]);

    return { copied, copy };
}

function CopyButton({ text, label }: { text: string; label: string }) {
    const { t } = useTranslation();
    const { copied, copy } = useCopy(text);
    return (
        <>
            <button
                type="button"
                className="apps-copy"
                onClick={copy}
                aria-label={copied ? t("apps.copied") : label}
                title={copied ? t("apps.copied") : label}
            >
                {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
            </button>
            <span className="sr-only" aria-live="polite">{copied ? t("apps.copied") : ""}</span>
        </>
    );
}

/**
 * App Store-style detail view. A bottom sheet on phones (drag the grabber down
 * to dismiss), the site's right-hand `.panel` everywhere else, so it matches the
 * Portfolio project panel.
 */
function AppSheet({ app, onClose, asSheet }: { app: AppEntry | null; onClose: () => void; asSheet: boolean }) {
    const { t } = useTranslation();
    const reducedMotion = usePrefersReducedMotion();
    const titleId = useId();
    const dragControls = useDragControls();
    const closeRef = useRef<HTMLButtonElement>(null);
    const isOpen = app !== null;
    useScrollLock(isOpen);

    useEffect(() => {
        if (!isOpen) return;
        // Return focus to whatever opened the sheet (icon or tile) on close.
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        closeRef.current?.focus();
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("keydown", onKey);
            opener?.focus();
        };
    }, [isOpen, onClose]);

    const onDragEnd = (_: unknown, info: PanInfo) => {
        if (info.offset.y > 120 || info.velocity.y > 600) onClose();
    };

    const hidden = reducedMotion ? { opacity: 0 } : asSheet ? { y: "100%" } : { x: "100%" };
    const shown = reducedMotion ? { opacity: 1 } : asSheet ? { y: 0 } : { x: 0 };

    const features = app ? (t(`apps.items.${app.id}.features`, { returnObjects: true }) as string[]) : [];

    return createPortal(
        <AnimatePresence>
            {app && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: reducedMotion ? 0.15 : 0.35 }}
                        onClick={onClose}
                        aria-hidden="true"
                        className="panel-backdrop"
                    />
                    <motion.div
                        key={app.id}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        className={`panel app-sheet${asSheet ? " app-sheet-bottom" : ""}`}
                        initial={hidden}
                        animate={shown}
                        exit={hidden}
                        transition={{ duration: reducedMotion ? 0.15 : 0.45, ease: [0.32, 0.72, 0, 1] }}
                        drag={asSheet && !reducedMotion ? "y" : false}
                        dragListener={false}
                        dragControls={dragControls}
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={{ top: 0, bottom: 0.6 }}
                        onDragEnd={onDragEnd}
                    >
                        {asSheet && (
                            <div
                                className="app-sheet-grabber"
                                onPointerDown={(event) => dragControls.start(event)}
                                aria-hidden="true"
                            >
                                <span />
                            </div>
                        )}

                        {/* Floats over the scrolling content, so the header can scroll
                            away like an App Store page instead of eating the sheet. */}
                        <button
                            ref={closeRef}
                            type="button"
                            onClick={onClose}
                            className="app-sheet-close"
                            aria-label={t("common.close")}
                        >
                            <FiX aria-hidden="true" />
                        </button>

                        <div className="panel-body">
                            <header className="app-sheet-header">
                                <AppIcon app={app} size="lg" />
                                <div className="app-sheet-heading">
                                    <p className="app-sheet-kind">{t(`apps.items.${app.id}.kind`)}</p>
                                    <h2 id={titleId} className="app-sheet-title">{app.name}</h2>
                                    <p className="app-sheet-tagline">{t(`apps.items.${app.id}.tagline`)}</p>
                                    <AppLink app={app} className="app-sheet-get">
                                        {app.webUrl === undefined ? t("apps.get") : t("apps.open")}
                                    </AppLink>
                                </div>
                            </header>

                            <dl className="app-sheet-facts">
                                <div>
                                    <dt>{t("apps.facts.status")}</dt>
                                    <dd><StatusLabel app={app} /></dd>
                                </div>
                                <div>
                                    <dt>{t("apps.facts.platform")}</dt>
                                    <dd>{app.platforms.map((platform) => t(`apps.platforms.${platform}`)).join(" · ")}</dd>
                                </div>
                                <div>
                                    <dt>{t("apps.facts.since")}</dt>
                                    <dd>{app.year}</dd>
                                </div>
                                <div>
                                    <dt>{t("apps.facts.language")}</dt>
                                    <dd>{app.stack[0]}</dd>
                                </div>
                            </dl>

                            {app.preview && (
                                <div className="app-sheet-preview">
                                    <img src={app.preview} alt={t("apps.previewAlt", { name: app.name })} loading="lazy" decoding="async" />
                                </div>
                            )}

                            <div className="panel-block">
                                <h3 className="panel-label">{t("apps.sections.about")}</h3>
                                <p>{t(`apps.items.${app.id}.about`)}</p>
                            </div>

                            {features.length > 0 && (
                                <div className="panel-block">
                                    <h3 className="panel-label">{t("apps.sections.features")}</h3>
                                    <ul className="panel-list">
                                        {features.map((feature) => (
                                            <li key={feature}>{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {app.install && (
                            <div className="panel-block">
                                <h3 className="panel-label">{t("apps.sections.install")}</h3>
                                <ol className="app-sheet-steps">
                                    {app.install.map((command, index) => (
                                        <li key={command}>
                                            <span className="app-sheet-step-no" aria-hidden="true">{index + 1}</span>
                                            <code>{command}</code>
                                            <CopyButton text={command} label={t("apps.copyCommand", { step: index + 1 })} />
                                        </li>
                                    ))}
                                </ol>
                                <p className="app-sheet-note">{t(`apps.items.${app.id}.installNote`)}</p>
                            </div>
                            )}

                            <div className="panel-block">
                                <h3 className="panel-label">{t("apps.sections.builtWith")}</h3>
                                <ul className="panel-chips">
                                    {app.stack.map((tech) => (
                                        <li key={tech}>{tech}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="panel-footer">
                            <AppLink app={app} className="button button-dark">
                                {app.webUrl === undefined && <BsGithub aria-hidden="true" />}
                                <span>{app.webUrl === undefined ? t("apps.viewOnGithub") : t("apps.openApp", { name: app.name })}</span>
                                {app.webUrl !== undefined && isInSiteRoute(app.webUrl) ? <FiArrowRight aria-hidden="true" /> : <FiArrowUpRight aria-hidden="true" />}
                            </AppLink>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body,
    );
}
